import { providePrimeNG } from 'primeng/config'
import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'

import { CommonModule } from '@angular/common'
import { AuthService } from './common/services/auth.service'
import { firstValueFrom } from 'rxjs'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withJsonpSupport } from '@angular/common/http'
import { CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { BrowserModule } from '@angular/platform-browser'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouteReuseStrategy } from '@angular/router'
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin'
import { LOCAL_STORAGE_ENGINE, NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin'
import { NgxsModule } from '@ngxs/store'
import { SpOverlayModule } from '@sp-common/saraplus-ngx-common'
import { SaraPlusFormModule } from '@sp-forms/saraplus-ngx-forms'
import { PdfViewerModule } from 'ng2-pdf-viewer'
import { DialogService } from 'primeng/dynamicdialog'
import { environment } from './../environments/environment'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'
import { CountdownDialogComponent } from './common/components/countdowndialog/countdowndialog.component'
import { CountdownTimerComponent } from './common/components/countdowntimer/countdown-timer.component'
import { EncryptDecryptComponent } from './common/components/encrypt-decrypt/encrypt-decrypt.component'
import { SpinnerComponent } from './common/components/spinner/spinner.component'
import { AuthHeaderInterceptor } from './common/interceptors/auth-header.interceptor'
import { ApiStateInterceptor } from './common/interceptors/generic-api-state.interceptor'
import { HttpErrorInterceptor } from './common/interceptors/http-error.interceptor'
import { LoadingIndicatorInterceptor } from './common/interceptors/loading-indicator.interceptor'
import { PayloadEncryptionInterceptor } from './common/interceptors/payload-encryption.interceptor'
import { AppBaseModule } from './common/modules/app-base.module'
import { AppLayoutModule } from './common/modules/app-layout/app-layout.module'
import { PrimeNGModule } from './common/modules/primeng.module'
import { AppRouteReuseStrategy } from './common/services/app-reroute-strategy'
import { CountdownTimerService } from './common/services/countdown-timer.service'
import { DataService } from './common/services/data.service'
import { ExternalDataService } from './common/services/external-data.service'
import { AppState } from './common/store/state/app/app.state'
import { LoginFlowState } from './common/store/state/login-flow/login-flow.state'
import { UserState } from './common/store/state/user/user.state'
import { AnalyticsModule } from './features/analytics/analytics.module'
import { CreateNewPasswordComponent } from './features/identity/forgot-password/create-new-password/create-new-password.component'
import { ForgotPasswordComponent } from './features/identity/forgot-password/forgot-password.component'
import { UserManagementComponent } from './features/identity/user-management/user-management.component';
import { AddUserComponent } from './features/identity/user-management/add-user/add-user.component';
import { TemporaryPasscodeComponent } from './features/identity/forgot-password/temporary-passcode/temporary-passcode.component'
import { LoginComponent } from './features/identity/login/login.component'
import { MfaCollectionComponent } from './features/identity/mfa/mfa-collection/mfa-collection.component'
import { MfaSelectionComponent } from './features/identity/mfa/mfa-selection/mfa-selection.component'
import { TotpSetupComponent } from './features/identity/mfa/totp-setup/totp-setup.component'
import { TotpVerifyComponent } from './features/identity/mfa/totp-verify/totp-verify.component'
import { QRCodeComponent } from 'angularx-qrcode'
import { MfaVerificationDialogComponent } from './features/identity/mfa/mfa-verification-dialog/mfa-verification-dialog.component'
import { MfaVerificationComponent } from './features/identity/mfa/mfa-verification/mfa-verification.component'
import { MyProfileComponent } from './features/identity/my-profile/my-profile.component'
import { SecurityQaComponent } from './features/identity/security-qa/security-qa.component'
import { UserCheckinComponent } from './features/identity/user-checkin/user-checkin.component'
import { WelcomeComponent } from './features/identity/welcome/welcome.component'
import { CalendarComponent } from './common/components/calendar/calendar.component'
import { SlotBookingComponent } from './common/components/booking/slot-booking.component'
import { ActionBarComponent } from './common/components/action-bar/action-bar.component'
import { GlobalErrorHandler } from './common/handlers/global-error.handler'
import { OrderStepGuard } from './common/guards/order-step.guard';
import { SamlAuthComponent } from './features/identity/saml-auth/saml-auth.component';
import { SsoCallbackComponent } from './features/identity/sso-callback/sso-callback.component';
import { InputOtpFixDirective } from './common/directives/input-otp-fix.directive'
import { CustomerSearchComponent } from './features/customer-search/customer-search.component'
import { CustomerLookupComponent } from './features/customer-search/customer-lookup/customer-lookup.component';
import { LogoutComponent } from './features/identity/logout/logout.component';
import { LicAuthComponent } from './features/identity/lic-auth/lic-auth.component'
import { TpvComponent } from './features/tpv/tpv.component'
import { OrderResumeState } from './common/store/state/lic/order-resume.state'

const saraTheme = definePreset(Aura, {
    // PrimeFlex blue utilities share the pre-migration palette across all screens.
    primitive: { blue: {
        50: '#f4fafe', 100: '#cae6fc', 200: '#a0d2fa', 300: '#75bef8',
        400: '#4baaf5', 500: '#2196f3', 600: '#1c80cf', 700: '#1769aa',
        800: '#125386', 900: '#0d3c61'
    } },
    semantic: { primary: {
        // Retain the legacy ramp for PrimeFlex text-primary-* and other consumers.
        50: '#f4fafe', 100: '#cae6fc', 200: '#a0d2fa', 300: '#75bef8',
        400: '#4baaf5', 500: '#2196f3', 600: '#1c80cf', 700: '#1769aa',
        800: '#125386', 900: '#0d3c61',
        950: '{blue.950}'
    }, colorScheme: { light: { primary: {
        color: '#2196f3', contrastColor: '#ffffff',
        hoverColor: '#0b7ad1', activeColor: '#0967af'
    } } } },
    components: {
        toast: {
            root: { borderRadius: '3px', borderWidth: '0 0 0 6px' },
            icon: { size: '2rem' },
            content: { padding: '1rem', gap: '1rem' },
            text: { gap: '0' },
            summary: { fontWeight: '700', fontSize: '1rem' },
            detail: { fontWeight: '400', fontSize: '1rem' },
            closeButton: { width: '2rem', height: '2rem' },
            colorScheme: { light: {
                root: { blur: '0' },
                info: { background: '#b3e5fc', borderColor: '#0891cf', color: '#044868',
                    detailColor: '#044868', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                success: { background: '#c8e6c9', borderColor: '#439446', color: '#224a23',
                    detailColor: '#224a23', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                warn: { background: '#ffecb3', borderColor: '#d9a300', color: '#6d5100',
                    detailColor: '#6d5100', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } },
                error: { background: '#ffd1ce', borderColor: '#d9362b', color: '#73000c',
                    detailColor: '#73000c', shadow: '0 2px 4px -1px rgba(0,0,0,0.2), 0 4px 5px 0 rgba(0,0,0,0.14), 0 1px 10px 0 rgba(0,0,0,0.12)',
                    closeButton: { hoverBackground: 'rgba(255,255,255,0.5)',
                        focusRing: { color: '#a6d5fa', shadow: '0 0 0 0.2rem #a6d5fa' } } }
            } }
        },
        message: {
            colorScheme: { light: { info: {
                color: '#044868',
                outlined: { color: '#044868' },
                simple: { color: '#044868' }
            } } }
        },
        button: {
            root: { label: { fontWeight: 'inherit' } }
        },
        select: {
            root: {
                background: '#ffffff', borderColor: '#ced4da',
                hoverBorderColor: '#2196f3', focusBorderColor: '#2196f3',
                color: '#495057', placeholderColor: '#6c757d', shadow: 'none'
            },
            dropdown: { color: '#6c757d' }
        },
        toggleswitch: {
            colorScheme: { light: {
                handle: { disabledBackground: '#ffffff' }
            } }
        },
        radiobutton: {
            root: {
                width: '20px', height: '20px', background: '#ffffff',
                borderColor: '#ced4da', hoverBorderColor: '#2196f3',
                focusBorderColor: '#2196f3', checkedBorderColor: '#2196f3',
                checkedHoverBorderColor: '#0b7ad1', checkedFocusBorderColor: '#2196f3',
                checkedBackground: 'transparent', checkedHoverBackground: 'transparent',
                disabledBackground: '#ffffff', checkedDisabledBorderColor: '#ced4da',
                invalidBorderColor: '#f44336', shadow: 'none',
                focusRing: { width: '0', shadow: '0 0 0 0.2rem #a6d5fa' }
            },
            icon: {
                size: '12px', checkedColor: '#237ec0',
                checkedHoverColor: '#237ec0', disabledColor: '#237ec0'
            }
        },
        accordion: {
            panel: { borderWidth: '0', borderColor: '#dee2e6' },
            header: {
                padding: '1rem', fontWeight: '600', borderWidth: '1px',
                borderColor: '#dee2e6', borderRadius: '3px',
                color: '#495057', hoverColor: '#495057',
                activeColor: '#495057', activeHoverColor: '#495057',
                background: '#f8f9fa', hoverBackground: '#e9ecef',
                activeBackground: '#f8f9fa', activeHoverBackground: '#e9ecef',
                first: { topBorderRadius: '10px', borderWidth: '1px' },
                last: { bottomBorderRadius: '3px', activeBottomBorderRadius: '0' },
                toggleIcon: {
                    color: '#495057', hoverColor: '#495057',
                    activeColor: '#495057', activeHoverColor: '#495057'
                },
                focusRing: { width: '0', shadow: 'inset 0 0 0 0.2rem #a6d5fa' }
            },
            content: {
                padding: '1rem', borderWidth: '0 1px 1px',
                borderColor: '#dee2e6', background: '#ffffff', color: '#495057'
            }
        },
        badge: {
            root: { borderRadius: '10px' },
            colorScheme: {
                light: {
                    // Preserve the status palette from the previous Saga Blue theme.
                    success: { background: '#689f38', color: '#ffffff' },
                    danger: { background: '#d32f2f', color: '#ffffff' },
                    info: { background: '#0288d1', color: '#ffffff' }
                }
            }
        }
    }
})

@NgModule({
    declarations: [
        AppComponent,
        CountdownDialogComponent,
        CountdownTimerComponent,
        EncryptDecryptComponent,
        SpinnerComponent,
        LoginComponent,
        ForgotPasswordComponent,
        TemporaryPasscodeComponent,
        CreateNewPasswordComponent,
        MfaCollectionComponent,
        MfaVerificationComponent,
        MfaSelectionComponent,
        TotpSetupComponent,
        TotpVerifyComponent,
        SecurityQaComponent,
        WelcomeComponent,
        MyProfileComponent,
        MfaVerificationDialogComponent,
        UserCheckinComponent,
        SamlAuthComponent,
        SsoCallbackComponent,
        UserManagementComponent,
        AddUserComponent,
        CustomerSearchComponent,
        CustomerLookupComponent,
        LogoutComponent,
        LicAuthComponent,
        TpvComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    bootstrap: [AppComponent],
    imports: [AnalyticsModule,
        QRCodeComponent,
        AppBaseModule,
        AppLayoutModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        BrowserModule,
        NgxsModule.forRoot([UserState, AppState, LoginFlowState, OrderResumeState], { developmentMode: !environment.production }),
        NgxsReduxDevtoolsPluginModule.forRoot(),
        NgxsStoragePluginModule.forRoot({
            keys: [{
                key: UserState,
                engine: LOCAL_STORAGE_ENGINE
            }, {
                key: AppState,
                engine: LOCAL_STORAGE_ENGINE
            }, {
                key: LoginFlowState,
                engine: LOCAL_STORAGE_ENGINE
            },
            {
                key: OrderResumeState,
                engine: SESSION_STORAGE_ENGINE
            }]
        }),
        PrimeNGModule,
        ReactiveFormsModule,
        PdfViewerModule,
        SaraPlusFormModule,
        SpOverlayModule,
        CommonModule,
        CalendarComponent,
        SlotBookingComponent,
        ActionBarComponent,
        InputOtpFixDirective],
    providers: [
        providePrimeNG({ theme: { preset: saraTheme, options: { darkModeSelector: false } } }),
        provideAppInitializer(() => inject(AuthService).restorePersistedSession()),
        // Global feature toggles from the database before the first page renders (at most 3 seconds).
        provideAppInitializer(() => firstValueFrom(inject(AuthService).loadGlobalFeatureToggles())),
        ExternalDataService,
        DialogService,
        CountdownTimerService,
        { provide: HTTP_INTERCEPTORS, useClass: AuthHeaderInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: PayloadEncryptionInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoadingIndicatorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: HttpErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: ApiStateInterceptor, multi: true },
        { provide: RouteReuseStrategy, useClass: AppRouteReuseStrategy },
        DataService,
        provideHttpClient(withInterceptorsFromDi(), withJsonpSupport()),
        {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler
        },
        OrderStepGuard
    ]
})
export class AppModule { }
